import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, from, lastValueFrom } from 'rxjs';
import { IThemeConfig, ThemeConfig } from '../model';
import { THEMES_CONFIG } from '../tokens';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  readonly theme = signal<IThemeConfig | undefined>(undefined);

  readonly #document = inject(DOCUMENT);
  // readonly #rootEl = this.#document.documentElement;
  readonly #config: ThemeConfig = inject(THEMES_CONFIG);
  // readonly #renderer = inject(RendererFactory2).createRenderer(null, null);

  get rootElement(): HTMLElement {
    return this.#document.querySelector('body');
  }

  constructor() {}

  /**
   * select theme and pull in its css
   */
  loadTheme(themeName: string): Promise<boolean> {
    return lastValueFrom(this.loadThemeFile(themeName));
  }

  private getFullThemeName(themeConfig: IThemeConfig) {
    const { themeName, themeSuffix } = themeConfig;

    if (themeSuffix) {
      return `${themeName}-${themeSuffix}`;
    }

    return `${themeName}`;
  }

  private loadThemeFile(fileName: string): Observable<boolean> {
    const themeConfig = this.extendTheme(fileName);
    const themeName = this.getFullThemeName(themeConfig);

    const themePromise = new Promise<boolean>((resolve) => {
      this.createDomHeadEl(themeConfig, themeName);

      this.replaceThemeClass(themeName);

      this.theme.set(themeConfig);

      resolve(true);
    });
    return from(themePromise);
  }

  private createDomHeadEl(themeConfig: IThemeConfig, fullThemeName: string) {
    const themeLink = this.#document.getElementById(themeConfig.themeFileId) as HTMLLinkElement;
    if (themeLink) {
      themeLink.href = `${fullThemeName}.css`;
    } else {
      const head = this.#document.getElementsByTagName('head')[0];
      const style = this.#document.createElement('link');
      style.id = themeConfig.themeFileId;
      style.rel = 'stylesheet';
      style.href = `${fullThemeName}.css`;

      head.appendChild(style);
    }
  }

  private extendTheme(name: string): IThemeConfig {
    const { themeName, themeFileId, themeSuffix, themeRoot, themeSource } = this.#config;
    return Object.assign({}, { themeName, themeFileId, themeSuffix, themeRoot, themeSource }, { themeName: name });
  }

  private replaceThemeClass(themeName: string) {
    const themeClass = `${themeName}`;
    const classList: DOMTokenList = this.rootElement.classList;

    const themeClassesToRemove = Array.from(classList).filter((item: string) => themeName /*item.includes('-theme')*/);
    if (themeClassesToRemove.length) {
      classList.remove(...themeClassesToRemove);
    }
    classList.add(themeClass);
  }
}
